import { Stack } from '@startuptools/ui/Stack';
import {
  useMeetingSetApprovedByMeetingMutation,
  usePriorMeetingsApprovalSuspenseQuery,
} from './PriorMeetingsApproval.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { isNil } from 'lodash-es';
import { GqlMeeting, GqlMeetingHeldKind } from '../../../../graphql/base-types.graphql';
import { Card } from '@startuptools/ui/Card';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { Typography } from '@startuptools/ui/Typography';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Trans, useLingui } from '@lingui/react';
import { DateTime } from 'luxon';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';

export const AiPriorMeetingsApproval = ({ meeting }: { meeting: Pick<GqlMeeting, 'id' | 'startTime'> }) => {
  const { i18n } = useLingui();
  const companyId = useCompanyId();

  const {
    data: {
      meetings: { models: meetings },
    },
  } = usePriorMeetingsApprovalSuspenseQuery({ variables: { companyId } });
  // eslint-disable-next-line sut/loading-mutation
  const [doApprove] = useMeetingSetApprovedByMeetingMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  const approvableMeetings = meetings.filter(
    m => (isNil(m.approvedByMeeting) || m.approvedByMeeting === meeting.id) && m.startTime < meeting.startTime,
  );

  const handleSetApproved = async (id: string, approve: boolean) => {
    await doApprove({
      variables: { companyId, input: { id, approvedByMeeting: meeting.id, setApproved: approve } },
      optimisticResponse: {
        meetingSetApprovedByMeeting: { id, approvedByMeeting: approve ? meeting.id : null, __typename: 'Meeting' },
      },
    });
  };

  return (
    <Stack gap={1}>
      {approvableMeetings.map(m => (
        <Card key={m.id}>
          <Stack direction="row" gap={1} justifyContent="space-between">
            <Checkbox
              checked={!isNil(m.approvedByMeeting)}
              onChange={e => handleSetApproved(m.id, e.target.checked)}
              label={
                <Typography>
                  <Trans
                    id="Approve - #{number} <dateFormat>{date}</dateFormat>"
                    components={{
                      dateFormat: (
                        <DateFormat
                          locale={i18n.locale}
                          format={
                            m.heldKind === GqlMeetingHeldKind.PerCapsulam
                              ? DateTime.DATE_SHORT
                              : DateTime.DATETIME_SHORT
                          }
                        />
                      ),
                    }}
                    values={{ date: m.startTime, number: m.number }}
                  />
                </Typography>
              }
            />
          </Stack>
        </Card>
      ))}
    </Stack>
  );
};
